import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "../../lib/api";
import { StoreLayout } from "../../components/StoreLayout";
import { Button } from "../../components/ui/button";
import { ArrowRight, MapPin, Wrench, CheckCircle } from "@phosphor-icons/react";
import { categoryImage } from "../../lib/storefront";

const IMG_BASE = API.replace(/\/api$/, "");

export default function CityPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/public/cities/${slug}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.city.city_name} Home Services — Fixigo`;
    }).catch(() => setNotFound(true));
  }, [slug]);

  const lineTarget = (line) => line.category ? `/services?category=${encodeURIComponent(line.category)}` : "/services";

  if (notFound) {
    return (
      <StoreLayout>
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-[Outfit] text-3xl font-bold">City not found</h1>
          <p className="text-[#475569] mt-3">We're not serving this city yet.</p>
          <Button className="mt-6 rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white" onClick={() => navigate("/")}>Back to home</Button>
        </div>
      </StoreLayout>
    );
  }
  if (!data) {
    return <StoreLayout><div className="max-w-7xl mx-auto px-5 py-24 text-center text-[#475569]">Loading…</div></StoreLayout>;
  }

  const c = data.city;

  return (
    <StoreLayout>
      {/* Banner */}
      <section className="relative h-[280px] md:h-[360px] overflow-hidden" data-testid="city-banner">
        {data.banner_image_url
          ? <img src={`${IMG_BASE}${data.banner_image_url}`} alt={c.city_name} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-gradient-to-br from-[#4F46E5] to-[#1E293B]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-5 h-full flex flex-col justify-end pb-9">
          <div className="inline-flex items-center gap-1.5 text-white/85 text-sm"><MapPin size={14} weight="fill" /> {c.state_name}</div>
          <h1 className="font-[Outfit] text-4xl md:text-5xl font-extrabold text-white mt-1" data-testid="city-name">
            Home Services in {c.city_name}
          </h1>
          <p className="text-white/85 mt-2 max-w-xl">Verified Fixigo professionals across {c.city_name}. Upfront pricing, pay after service.</p>
        </div>
      </section>

      {/* Available services */}
      <section className="max-w-7xl mx-auto px-5 py-14 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Available in {c.city_name}</p>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">Services you can book</h2>
          </div>
          <Button className="rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2" onClick={() => navigate("/services")} data-testid="city-explore-all">
            Explore all services <ArrowRight size={16} weight="bold" />
          </Button>
        </div>

        {data.services.length === 0 ? (
          <div className="mt-10 bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-8 text-center">
            <p className="text-[#9A3412] font-semibold">We're setting up services in {c.city_name}. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-9" data-testid="city-services-grid">
            {data.services.map((s) => (
              <button key={s.slug} onClick={() => navigate(lineTarget(s))} data-testid={`city-service-${s.slug}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden text-left shadow-sm hover:shadow-xl transition-all duration-300">
                <img src={categoryImage(s.category || s.label)} alt={s.label} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-1.5 text-white">
                    <Wrench size={15} weight="fill" className="text-[#C7D2FE]" />
                    <h3 className="font-[Outfit] font-bold text-base leading-tight">{s.label}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#C7D2FE] mt-2 group-hover:gap-2 transition-all duration-200">
                    Book Now <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm font-medium text-[#475569]">
          <div className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-[#166534]" /> Serving {data.pincode_count} pincode{data.pincode_count !== 1 ? "s" : ""} in {c.city_name}</div>
        </div>
      </section>
    </StoreLayout>
  );
}
